import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { ACS_FORMS } from './acsConfig';
import './acsForms.css';
import FormPdfPreview from '../formpdfpreview';

const DEFAULT_SIGNATURE = 'https://robopower.xyz/filemanager/acsforms/authorization/ram.png';

export function SignatureField({ label, value, onChange, reusable, onRestore, defaultSignature = DEFAULT_SIGNATURE }) {
  const canvas = useRef(null);
  const drawing = useRef(false);
  const [editing, setEditing] = useState(false);
  const [hasInk, setHasInk] = useState(false);
  const point = event => {
    const rect = canvas.current.getBoundingClientRect();
    return [(event.clientX - rect.left) * 600 / rect.width, (event.clientY - rect.top) * 180 / rect.height];
  };
  return <section className="acs-signature">
    <h4>{label}</h4>
    {editing ? <>
      <p>Draw your signature in the box using a mouse, pen, or touch.</p>
      <canvas ref={canvas} width={600} height={180} aria-label={`${label} drawing area`}
        onPointerDown={event => {
          event.preventDefault();
          canvas.current.setPointerCapture(event.pointerId);
          drawing.current = true;
          const context = canvas.current.getContext('2d');
          context.beginPath();
          context.lineWidth = 2;
          context.lineCap = 'round';
          context.strokeStyle = '#172b3a';
          context.moveTo(...point(event));
        }}
        onPointerMove={event => {
          if (!drawing.current) return;
          const context = canvas.current.getContext('2d');
          context.lineTo(...point(event));
          context.stroke();
          setHasInk(true);
        }}
        onPointerUp={() => { drawing.current = false; }}
        onPointerCancel={() => { drawing.current = false; }} />
      <div className="acs-actions">
        <button type="button" onClick={() => { canvas.current.getContext('2d').clearRect(0, 0, 600, 180); setHasInk(false); }}>Clear Signature</button>
        <button type="button" className="acs-primary" disabled={!hasInk} onClick={() => { onChange(canvas.current.toDataURL('image/png')); setEditing(false); }}>Accept Signature</button>
        <button type="button" onClick={() => setEditing(false)}>Cancel Signing</button>
      </div>
    </> : <>
      {value ? <img src={value} alt={label} /> : <div className="acs-signature-empty">Signature Required</div>}
      <div className="acs-actions">
        <button type="button" onClick={() => { setEditing(true); setHasInk(false); }}>{value ? 'Replace Signature' : 'Sign Here'}</button>
        {reusable && reusable !== value && <button type="button" onClick={() => onChange(reusable)}>Use previous client signature</button>}
        {value && <button type="button" className="acs-danger" onClick={() => onChange('')}>Clear</button>}
        {/* {onRestore && <button type="button" disabled={value === defaultSignature} onClick={onRestore}>Restore default authorization signature</button>} */}
      </div>
    </>}
  </section>;
}

export default function ClientAgreements({ clientid, dealerid, client, onBack, onComplete, onClientSignatureSaved }) {
  const [active, setActive] = useState(0);
  const workspaceRef = useRef(null);
  useEffect(() => {
    if (workspaceRef.current) workspaceRef.current.scrollTop = 0;
  }, [active]);
  const [drafts, setDrafts] = useState(() => ACS_FORMS.map(config => ({
    values: Object.fromEntries(config.fields.map(field => [field.key, field.initial])),
    checklist: Object.fromEntries(config.checklist.map(([key]) => [key, 0])),
    customerSignature: '', customerSignatureEdited: false, authorizedSignature: DEFAULT_SIGNATURE,
    authorizationName: 'RAM CHANDRA REDDY', state: client.state || '', registration: false, vehicleRegistrationPhoto: '',
    saved: false, revision: 0,
  })));
  const [busy, setBusy] = useState(false);
  const savingRef = useRef(false);
  const [message, setMessage] = useState(null);
  useEffect(() => {
    if (!message || message.error) return undefined;
    const timer = setTimeout(() => setMessage(null), 5000);
    return () => clearTimeout(timer);
  }, [message]);
  const [previousSignature, setPreviousSignature] = useState('');
  const clientSnapshot = JSON.stringify(client);
  const previousClient = useRef(clientSnapshot);
  useEffect(() => {
    if (previousClient.current === clientSnapshot) return;
    const oldClient = JSON.parse(previousClient.current);
    const nextClient = JSON.parse(clientSnapshot);
    setDrafts(items => items.map(item => ({ ...item, saved: false,
      state: item.state === oldClient.state ? nextClient.state || '' : item.state,
    })));
    previousClient.current = clientSnapshot;
  }, [clientSnapshot]);
  const config = ACS_FORMS[active];
  const draft = drafts[active];
  const update = changes => {
    setDrafts(items => items.map((item, index) => index === active ? { ...item, ...changes,
      ...('customerSignature' in changes ? { customerSignatureEdited: true } : {}), saved: false } : item));
    setMessage(null);
  };
  const navigate = index => { setActive(index); setMessage(null); };
  const handleVehicleRegistrationPhoto = event => {
  const file = event.target.files?.[0];
  if (!file) return;
  if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type) || file.size > 5 * 1024 * 1024) {
    setMessage({ error: true, text: 'Choose a JPG, PNG, or WebP image smaller than 5 MB.' });
    event.target.value = '';
    return;
  }

  const reader = new FileReader();

  reader.onloadend = () => {
    if (reader.error) {
      setMessage({ error: true, text: 'Unable to read the registration image. Please try again.' });
      return;
    }
    update({
      vehicleRegistrationPhoto: reader.result,
      registration: true,
    });
  };

  reader.readAsDataURL(file);
};
  const save = async advance => {
    if (savingRef.current) return;
    setMessage(null);
    if (!clientid || !dealerid) return setMessage({ error: true, text: 'Save the client details before completing ACS forms.' });
    if (!draft.customerSignature || !draft.authorizedSignature) return setMessage({ error: true, text: 'Please provide both the Customer Signature and the Authorized Representative Signature to continue.' });
    if (active === 0 && (!draft.authorizationName.trim() || !draft.state.trim())) return setMessage({ error: true, text: 'Enter the authorization name and state.' });
    if (Object.values(draft.values).some(value => value === '' || !Number.isFinite(Number(value)) || Number(value) < 0)) return setMessage({ error: true, text: 'Enter a valid, non-negative amount in each fee field.' });
    const endpoint = config.endpoint || process.env.REACT_APP_ACS_STANDARD_FEE_POST_URL;
    if (!endpoint) return setMessage({ error: true, text: 'Standard Fee saving is not configured. Your entries are retained; the save endpoint must be configured before you can finish.' });
    const payload = {
      clientid: Number(clientid), dealerid: Number(dealerid),
      ...Object.fromEntries(Object.entries(draft.values).map(([key, value]) => [key, Number(value)])),
      ...draft.checklist, customer_sign: draft.customerSignature, authorization_sign: draft.authorizedSignature,
      ...(active === 0 ? { authorization_name: draft.authorizationName.trim(), state: draft.state.trim().toUpperCase(), vehicle_registration_attached: draft.registration ? 1 : 0, vehicleregistration_photo: draft.registration ? draft.vehicleRegistrationPhoto || '' : '', } : {}),
    };
    savingRef.current = true;
    setBusy(true);
    try {
      const response = await axios.post(endpoint, payload);
      if (response.data?.success === false || response.data?.status === false || response.data?.error || (typeof response.data === 'string' && /error|fail|invalid/i.test(response.data))) throw new Error(response.data?.message || 'The form could not be saved.');
      setDrafts(items => items.map((item, index) => index === active
        ? { ...item, saved: true, revision: item.revision + 1 }
        : index > active && !item.customerSignature && !item.customerSignatureEdited
          ? { ...item, customerSignature: draft.customerSignature, saved: false }
          : item));
      setPreviousSignature(draft.customerSignature);
      onClientSignatureSaved?.(draft.customerSignature);
      setMessage({ text: `${config.title} saved successfully.` });
      if (advance && active < ACS_FORMS.length - 1) navigate(active + 1);
      if (advance && active === ACS_FORMS.length - 1) {
        if (drafts.slice(0, -1).every(item => item.saved)) onComplete();
        else setMessage({ error: true, text: 'Save each of the previous forms before continuing to the installation form.' });
      }
    } catch (error) {
      setMessage({ error: true, text: error.response?.data?.message || error.message || 'Unable to save. Please try again.' });
    } finally { setBusy(false); savingRef.current = false; }
  };
  const previewUrl = `${config.preview}?clientid=${encodeURIComponent(clientid)}${draft.revision ? `&_revision=${draft.revision}` : ''}`;
  const continueToNext = () => {
    if (!draft.saved || busy) return;
    if (active < ACS_FORMS.length - 1) navigate(active + 1);
    else if (drafts.every(item => item.saved)) onComplete();
  };
  return <div className="acs-wizard">
    <header className="acs-heading"><div><span className="acs-eyebrow">CLIENT ONBOARDING</span><h2>Document Form & checklists</h2><p>Review details, confirm fees, and sign each document.</p></div><span className="acs-count">{drafts.filter(item => item.saved).length} of 5 saved</span></header>
    <nav className="acs-steps" aria-label="ACS forms">{ACS_FORMS.map((item, index) => <button type="button" key={item.key} disabled={busy || (index > active && !drafts.slice(0, index).every(item => item.saved))} aria-current={active === index ? 'step' : undefined} onClick={() => navigate(index)}><span>{drafts[index].saved ? '✓' : index + 1}</span><div>{item.title}<small>{drafts[index].saved ? 'Saved' : active === index ? 'In progress' : 'Pending'}</small></div></button>)}</nav>
    <div className="acs-workspace" ref={workspaceRef}>
      <section className="acs-editor" aria-label={config.title}>
        <h3>{active + 1}. {config.title}</h3>
        <p className="acs-description">{config.checklist.length ? 'Check each item after reviewing it with the client.' : 'Review the default fees and update them for this client.'}</p>
        <fieldset disabled={busy}>
          {active === 0 && <div className="acs-field-grid"><label>Authorization name<input value={draft.authorizationName} onChange={e => update({ authorizationName: e.target.value })} /></label><label>Agreement state<input maxLength={2} value={draft.state} onChange={e => update({ state: e.target.value.toUpperCase() })} /></label></div>}
          <div className="acs-field-grid">{config.fields.map(field => <label key={field.key}>{field.label}<input type="number" min="0" step="0.01" value={draft.values[field.key]} onChange={e => update({ values: { ...draft.values, [field.key]: e.target.value } })} /></label>)}</div>
          <div className="acs-checklist">{config.checklist.map(([key, label]) => <label key={key}><input type="checkbox" checked={draft.checklist[key] === 1} onChange={e => update({ checklist: { ...draft.checklist, [key]: e.target.checked ? 1 : 0 } })} /><span>{label}</span></label>)}</div>
          {active === 0 && <label className="acs-check-row"><input type="checkbox" checked={draft.registration} onChange={e => update({ registration: e.target.checked })} />Vehicle registration attached</label>}
          {active === 0 && draft.registration && (
  <div style={{ marginTop: '12px', marginBottom: '15px' }}>
    <label htmlFor="acs-registration-photo" style={{ display: 'block', fontWeight: '600', marginBottom: '8px' }}>
      Vehicle Registration Photo
    </label>

    <input
      id="acs-registration-photo"
      type="file"
      accept="image/jpeg,image/png,image/webp"
      onChange={handleVehicleRegistrationPhoto}
    />

    {draft.vehicleRegistrationPhoto && (
      <div style={{ marginTop: '10px' }}>
        <img
          src={draft.vehicleRegistrationPhoto}
          alt="Vehicle Registration"
          style={{
            maxWidth: '100%',
            maxHeight: '90px',
            objectFit: 'contain',
            border: '1px solid #ddd',
          }}
        />

        <div>
          <button
            type="button"
            onClick={() =>
              update({
                vehicleRegistrationPhoto: '',
                registration: false,
              })
            }
          >
            Remove Photo
          </button>
        </div>
      </div>
    )}
  </div>
)}
          <div className="acs-signatures"><SignatureField key={`${active}-authorization`} label="Authorized Representative Signature" value={draft.authorizedSignature} onChange={authorizedSignature => update({ authorizedSignature })} onRestore={() => update({ authorizedSignature: DEFAULT_SIGNATURE })} />
          <SignatureField key={`${active}-client`} label={`${client.client || 'Client'} — Customer Signature`} value={draft.customerSignature} reusable={previousSignature} onChange={customerSignature => update({ customerSignature })} /></div>
        </fieldset>
      </section>
      <aside className="acs-preview"><header><div><h3>Document preview</h3><p>{draft.saved ? 'Updated saved document' : 'Current document. Save to update your changes.'}</p></div>{!!clientid && <a href={previewUrl} target="_blank" rel="noopener noreferrer">Open document ↗</a>}</header>{!!clientid ? <FormPdfPreview key={`${active}-${draft.revision}`} title={`${config.title} preview`} src={previewUrl} /> : <div className="acs-preview-empty"><span>▤</span><h4>{config.title}</h4><p>Save client details to view this document.</p></div>}</aside>
    </div>
    {message && <div className={`acs-message ${message.error ? 'acs-error' : ''}`} role={message.error ? 'alert' : 'status'}>{message.text}</div>}
    <footer className="acs-footer"><button type="button" disabled={busy} onClick={() => active === 0 ? onBack() : navigate(active - 1)}>← {active === 0 ? 'Client details' : 'Back'}</button><span>Step {active + 1} of 5</span><div className="acs-actions"><button type="button" disabled={busy} onClick={() => save(false)}>{busy ? 'Saving...' : 'Save Agreement'}</button>{draft.saved && <button type="button" className="acs-primary" disabled={busy} onClick={continueToNext}>{busy ? 'Saving…' : active === 4 ? 'Continue to installation →' : 'Continue →'}</button>}</div></footer>
  </div>;
}
